import Button from "react-bootstrap/Button";
import { MutableRefObject, useEffect, useMemo, useState } from "react";
import { ValidationTypes } from "@enums/validation-types";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { getPreviewDimensions } from "@utils/image-util";
import { CaptureStep } from "@idw-types/data/capture-step";
import { useSelector } from "react-redux";
import { getCaptureStepValidationResults } from "@redux/selectors/validation-results-selectors";
import { CaptureStepTypes } from "@enums/capture-step-types";
import { useAuditLogger } from "@hooks/use-audit-logger";
import { AuditLogTypes } from "@constants/audit-log-types";
import styles from "./image-preview-container.less";

type ImagePreviewContainerProps = {
    captureStep: CaptureStep;
    imageRef: MutableRefObject<HTMLImageElement>;
    imageUrl: string;
    imagePreviewLoaded: boolean;
    setImageUrl: (imageUrl: string) => void;
    setImagePreviewLoaded: (isLoaded: boolean) => void;
};

export const ImagePreviewContainer = (props: ImagePreviewContainerProps) => {
    const {
        captureStep,
        imageRef,
        imageUrl,
        imagePreviewLoaded,
        setImageUrl,
        setImagePreviewLoaded
    } = props;

    const { writeAuditLog } = useAuditLogger();

    const generateAuditLog = () => {
        switch (captureStep.type) {
            case CaptureStepTypes.DRIVER_LICENSE_FRONT:
            case CaptureStepTypes.DRIVER_LICENSE_BACK:
                writeAuditLog(AuditLogTypes.DCUI_DL_IMAGE_PREVIEWED);
                break;
            case CaptureStepTypes.FACE:
            case CaptureStepTypes.LIVENESS_CHECK:
                writeAuditLog(AuditLogTypes.DCUI_SELFIE_IMAGE_PREVIEWED);
                break;
            case CaptureStepTypes.ID_CARD_BACK:
            case CaptureStepTypes.ID_CARD_FRONT:
                writeAuditLog(AuditLogTypes.DCUI_DL_IMAGE_PREVIEWED);
                break;
            case CaptureStepTypes.PASSPORT:
                writeAuditLog(AuditLogTypes.DCUI_PASSPORT_IMAGE_PREVIEWED);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        generateAuditLog();
    }, []);

    const [hasRotated, setHasRotated] = useState(false);
    //@ts-ignore
    const captureStepValidationResults = useSelector(state => getCaptureStepValidationResults(state, captureStep.type));

    const rotateImage = (degrees: number) => {
        const canvas = document.getElementById("image-preview-canvas") as HTMLCanvasElement;
        canvas.width = imageRef.current.naturalHeight;
        canvas.height = imageRef.current.naturalWidth;

        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((degrees * Math.PI) / 180);
        ctx.drawImage(imageRef.current, -imageRef.current.naturalWidth / 2, -imageRef.current.naturalHeight / 2);
        const dataUrl = canvas.toDataURL("image/jpeg");
        setImageUrl(dataUrl);
    };

    const rotateImageLeft = () => {
        setHasRotated(true);
        setImagePreviewLoaded(false);
        rotateImage(-90);
    };

    const rotateImageRight = () => {
        setHasRotated(true);
        setImagePreviewLoaded(false);
        rotateImage(90);
    };

    const handleOnLoad = () => {
        const canvas = document.getElementById("image-preview-canvas") as HTMLCanvasElement;
        const imageDimensions = getPreviewDimensions(captureStep.type, imageRef.current.naturalWidth, imageRef.current.naturalHeight);
        canvas.width = imageDimensions.width;
        canvas.height = imageDimensions.height;

        const scale = Math.min(canvas.width / imageRef.current.naturalWidth, canvas.height / imageRef.current.naturalHeight);
        const x = (canvas.width / 2) - (imageRef.current.naturalWidth) * scale;
        const y = (canvas.height / 2) - (imageRef.current.naturalHeight) * scale;
        const ctx = canvas.getContext("2d");
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.drawImage(imageRef.current, x, y, imageRef.current.naturalWidth * scale, imageRef.current.naturalHeight * scale);

        setImagePreviewLoaded(true);
    };

    const showRotateOrientationButtons = useMemo(() => {
        if (hasRotated) {
            return true;
        }
        if (captureStepValidationResults[ValidationTypes.LANDSCAPE_ORIENTATION_DETECTION]) {
            return true; //todo- fix
        }
        if (captureStepValidationResults[ValidationTypes.PORTRAIT_ORIENTATION_DETECTION]) {
            return true; //todo - fix
        }

        return false;
    }, [hasRotated, captureStepValidationResults]);

    const additionalClassName = useMemo(() => {
        return captureStep.type === CaptureStepTypes.FACE ? styles.faceImagePreviewContainer : styles.documentImagePreviewContainer;
    }, [captureStep.type]);

    return (
        <div id={"image-preview"} className={classNames(styles.imagePreviewContainer, additionalClassName, "capture-preview-container")}>
            <img
                ref={imageRef}
                id="capture-preview"
                alt={"Captured Image"}
                className={styles.image}
                src={imageUrl}
                onLoad={handleOnLoad}
            />
            <canvas id={"image-preview-canvas"} className={styles.imagePreviewCanvas} />
            {showRotateOrientationButtons && (
                <div className={classNames(styles.rotateButtonsContainer, "rotation-action-container")}>
                    <div className={"rotation-buttons"}>
                        <Button id="rotate-left" disabled={!imagePreviewLoaded} onClick={rotateImageLeft}>
                            <FontAwesomeIcon icon={faUndo} />
                        </Button>
                        <div className={styles.spacer}></div>
                        <Button id="rotate-right" disabled={!imagePreviewLoaded} onClick={rotateImageRight}>
                            <FontAwesomeIcon icon={faUndo} className={"fa-flip-horizontal"} />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};